import {css, StyleSheet} from 'aphrodite';
import React, {Fragment} from 'react';

import LookupStepSeparatorIcon from 'lookup-config/LookupStepSeparatorIcon';
import {Schema} from 'schemas/SchemaConstants';
import {FONT_PROXIMA_TITLE} from 'styles/FontConstants';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {BLUE_LIGHT, GRAY_DARK, WHITE} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {LookupStepInfo} from './LookupConfigModalConstants';
import LookupConfigModalErrorIndicator from './LookupConfigModalErrorIndicator';
import LookupStepChip from './LookupStepChip';

// exported for test
export const DATA_TEST_ATTRS = {
  removeLookupStepButton: {component: 'LookupStepSectionHeader', element: 'removeLookupStepButton'},
  collapserChevron: {component: 'LookupStepSectionHeader', element: 'collapserChevron'},
};

interface LookupStepSectionHeaderProps {
  isCollapsed: boolean;
  setIsCollapsed: (val: boolean) => void;
  isActive: boolean;
  lookupStepGroup: Array<LookupStepInfo>;
  handleRemoveLookupStepGroup?: (() => void) | null;
  parentSchema: Schema | null;
  error: string | null;
}

export function LookupStepSectionHeader(props: LookupStepSectionHeaderProps): JSX.Element {
  const handleRemoveLookupStepGroup = props.handleRemoveLookupStepGroup;
  return (
    <div className={css(styles.lookupStepSectionHeader)}>
      <Button
        className={css(styles.collapserChevron)}
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        onClick={() => {
          props.setIsCollapsed(!props.isCollapsed);
        }}
        size={ButtonSize.SMALL}
        icon={props.isCollapsed ? 'chevron' : 'chevron-down'}
        dataTestAttrs={DATA_TEST_ATTRS.collapserChevron}
      />
      <span className={css(styles.lookupStepSectionTitle)}>Lookup step</span>
      {props.lookupStepGroup.map((lookupStepInfo, index) => (
        <Fragment key={index}>
          {index > 0 && <LookupStepSeparatorIcon index={index} lookupStepInfos={props.lookupStepGroup} />}
          <div className="u-mini-spacing-right">
            <LookupStepChip lookupStepInfo={lookupStepInfo} hasError={props.error != null} />
          </div>
        </Fragment>
      ))}
      {props.isActive ? (
        <Tooltip title="The object that the lookup is based on">
          <div className={css(styles.activeLookupStepIndicator)}>
            <BenchlingIcon className={css(styles.activeLookupStepIndicatorIcon)} icon="check" />
          </div>
        </Tooltip>
      ) : null}
      {props.error ? <LookupConfigModalErrorIndicator error={props.error} /> : null}
      {handleRemoveLookupStepGroup ? (
        <Button
          className={css(styles.removeLookupStepButton)}
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          onClick={handleRemoveLookupStepGroup}
          size={ButtonSize.SMALL}
          icon="close"
          dataTestAttrs={DATA_TEST_ATTRS.removeLookupStepButton}
        />
      ) : null}
    </div>
  );
}

const styles = StyleSheet.create({
  collapserChevron: {
    color: GRAY_DARK,
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
  },
  removeLookupStepButton: {
    color: GRAY_DARK,
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
    marginLeft: 'auto',
  },
  lookupStepSectionHeader: {
    marginLeft: '-9px',
    display: 'flex',
    alignItems: 'center',
  },
  lookupStepSectionTitle: {
    ...FONT_PROXIMA_TITLE,
    marginRight: '6px',
  },
  activeLookupStepIndicator: {
    display: 'inline-block',
    backgroundColor: BLUE_LIGHT,
    color: WHITE,
    borderRadius: '50%',
    width: '14px',
    height: '14px',
    textAlign: 'center',
    lineHeight: '14px',
  },
  activeLookupStepIndicatorIcon: {
    width: '8px',
    height: '14px',
    marginBottom: '0px',
  },
});
